import React, { useState, useEffect } from "react";

// WMO Weather Code → description + emoji
// https://open-meteo.com/en/docs#weathervariables
const WMO = {
  0:  { label: "Clear sky",          emoji: "☀️" },
  1:  { label: "Mainly clear",       emoji: "🌤️" },
  2:  { label: "Partly cloudy",      emoji: "⛅" },
  3:  { label: "Overcast",           emoji: "☁️" },
  45: { label: "Foggy",              emoji: "🌫️" },
  48: { label: "Icy fog",            emoji: "🌫️" },
  51: { label: "Light drizzle",      emoji: "🌦️" },
  53: { label: "Drizzle",            emoji: "🌦️" },
  55: { label: "Heavy drizzle",      emoji: "🌦️" },
  61: { label: "Light rain",         emoji: "🌧️" },
  63: { label: "Rain",               emoji: "🌧️" },
  65: { label: "Heavy rain",         emoji: "🌧️" },
  71: { label: "Light snow",         emoji: "🌨️" },
  73: { label: "Snow",               emoji: "❄️" },
  75: { label: "Heavy snow",         emoji: "❄️" },
  80: { label: "Light showers",      emoji: "🌦️" },
  81: { label: "Showers",            emoji: "🌧️" },
  82: { label: "Heavy showers",      emoji: "🌧️" },
  95: { label: "Thunderstorm",       emoji: "⛈️" },
  96: { label: "Thunderstorm + hail",emoji: "⛈️" },
  99: { label: "Severe thunderstorm",emoji: "⛈️" },
};

function getWmo(code) {
  return WMO[code] || { label: "Unknown", emoji: "🌡️" };
}

function formatDay(dateStr) {
  const d = new Date(dateStr + "T12:00:00");
  return d.toLocaleDateString("en-US", { weekday: "short" }).toUpperCase();
}

export default function Weather({ lat, lon }) {
  const [weather, setWeather]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  useEffect(() => {
    if (!lat || !lon) return;

    setLoading(true);
    setError(null);

    const params = new URLSearchParams({
      latitude:                   lat,
      longitude:                  lon,
      current:                    "temperature_2m,apparent_temperature,weathercode,windspeed_10m,relative_humidity_2m",
      daily:                      "weathercode,temperature_2m_max,temperature_2m_min,precipitation_probability_max",
      temperature_unit:           "fahrenheit",
      wind_speed_unit:            "mph",
      precipitation_unit:         "inch",
      timezone:                   "America/New_York",
      forecast_days:              "7",
    });

    fetch(`https://api.open-meteo.com/v1/forecast?${params}`)
      .then(r => {
        if (!r.ok) throw new Error("Weather service unavailable");
        return r.json();
      })
      .then(data => {
        setWeather(data);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, [lat, lon]);

  if (!lat || !lon) return null;

  if (loading) return (
    <div className="weather-widget weather-loading">
      <span className="weather-spinner" /> Loading weather…
    </div>
  );

  if (error) return (
    <div className="weather-widget weather-error">⚠️ {error}</div>
  );

  if (!weather) return null;

  const cur     = weather.current;
  const daily   = weather.daily;
  const wmo     = getWmo(cur.weathercode);

  return (
    <div className="weather-widget">
      {/* Current conditions */}
      <div className="weather-current">
        <span className="weather-emoji">{wmo.emoji}</span>
        <div className="weather-temps">
          <span className="weather-temp">{Math.round(cur.temperature_2m)}°F</span>
          <span className="weather-desc">{wmo.label}</span>
          <span className="weather-feels">
            Feels {Math.round(cur.apparent_temperature)}°F · {cur.relative_humidity_2m}% humidity · {Math.round(cur.windspeed_10m)} mph
          </span>
        </div>
      </div>

      {/* 5-day forecast */}
      <div className="weather-forecast">
        {daily.time.map((day, i) => {
          const fw = getWmo(daily.weathercode[i]);
          const rain = daily.precipitation_probability_max[i];
          return (
            <div key={day} className="weather-day">
              <span className="weather-day-name">{i === 0 ? "TODAY" : formatDay(day)}</span>
              <span className="weather-day-emoji">{fw.emoji}</span>
              {rain > 20 && <span className="weather-rain">💧{rain}%</span>}
              <span className="weather-day-hi">{Math.round(daily.temperature_2m_max[i])}°</span>
              <span className="weather-day-lo">{Math.round(daily.temperature_2m_min[i])}°</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
